<template>
  <v-stepper class="elevation-0 transparent" v-model="step">
    <v-stepper-header class="elevation-0">
      <v-stepper-step :complete="step > 1" step="1">Upload File</v-stepper-step>
      <v-divider></v-divider>
      <v-stepper-step :complete="step > 2" step="2"
        >Review Changes</v-stepper-step
      >
      <v-divider></v-divider>
      <v-stepper-step :complete="step > 3" step="3"
        >View Results</v-stepper-step
      >
    </v-stepper-header>
    <v-stepper-items class="remove-left-border">
      <v-stepper-content step="1">
        <v-row>
          <v-col class="col-12 col-xl-6">
            <p>
              To help with importing {{ entityName }}s, please download the
              sample file. The download sample file will allow you to utilize an
              excel document to input user data. Choose from one of the file
              presets. The required columns list will autopopulate based on the
              file preset selection. Once you have made your file selection,
              click the download button to access the sample file.
            </p>
            <p>
              In order to upload {{ entityName }}s, choose entity file to import
              and attach the completed sample file.
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <dialog-export :entityName="entityName">
              <template v-slot:default="{ on }">
                <v-btn
                  class="text-capitalize"
                  color="primary"
                  small
                  text
                  v-on="on"
                  >Download sample file</v-btn
                >
              </template>
            </dialog-export>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-file-input
              :rules="ruleList"
              accept=".xlsx"
              class="mb-4 file-input-override"
              data-cy="import-file-input"
              dense
              hide-details
              outlined
              placeholder="Choose entity file to import ..."
              prepend-icon
              prepend-inner-icon="fas fa-paperclip"
              ref="file"
              show-size
              v-model="fileUpload"
            ></v-file-input>
          </v-col>
        </v-row>
        <div class="d-flex justify-space-between">
          <v-btn
            outlined
            color="primary"
            small
            @click="$router.push({ name: `${entityName}List` })"
            >Back</v-btn
          >
          <v-btn
            :disabled="!fileUpload"
            color="primary"
            outlined
            data-cy="import-file-upload-button"
            small
            v-on:click="upload()"
            >Upload</v-btn
          >
        </div>
      </v-stepper-content>
      <v-stepper-content step="2">
        <v-row>
          <v-col class="col-12 col-xl-6">
            <p v-if="parseFail">
              Valet could not parse the uploaded excel file. Please reattempt to
              import users via downloading and editing one of the provided
              templates, or contact OCHIN Help Desk.
            </p>
            <p v-else>
              Please review the changes imported. Items with changes are
              identified in the "Has Changes" column. To review individual
              changes, click the row you would like to review. Once your review
              is complete, click "Submit" to finalize the changes.
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="mt-8 d-flex justify-space-between" v-if="parseFail">
              <v-btn outlined color="primary" small @click="step = 1"
                >Back</v-btn
              >
            </div>
            <table-confirm
              :entityList="entityListImport"
              :entityName="entityName"
              @canceled="step = 1"
              @submitted="
                value => {
                  entityResultList = value;
                  step = 3;
                }
              "
              v-else
            ></table-confirm>
          </v-col>
        </v-row>
      </v-stepper-content>
      <v-stepper-content step="3">
        <v-row>
          <v-col class="col-12 col-xl-6">
            <p>
              The following updates have been completed. Please review updates
              for any unsuccessful results.
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <table-result
              :entityList="entityResultList"
              :entityName="entityName"
              @canceled="step = 2"
              @completed="$router.push({ name: `${entityName}List` })"
            ></table-result>
          </v-col>
        </v-row>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import axios from "axios";
import { keyBy, get, reduce } from "lodash";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import DialogExport from "./components/DialogExport";
import TableConfirm from "./components/TableConfirm";
import TableResult from "./components/TableResult";

export default {
  components: {
    DialogExport,
    TableConfirm,
    TableResult,
  },
  computed: {
    attributeList() {
      const { entityName } = this;

      return this.filterAttribute({ entityName });
    },
    attributeMapByLabel() {
      const { attributeList } = this;

      return keyBy(attributeList, "label");
    },
    entityList() {
      const { entityName } = this;

      return get(this.$store.state, `${entityName}/list`);
    },
    entityListImport() {
      const {
        attributeMapByLabel,
        file: { content },
        entityMapByEmail,
      } = this;

      if (!content) {
        return [];
      }

      let entityListImport = content
        .map(entity =>
          reduce(
            entity,
            (acc, attributeValue, label) => {
              const attributeDefinition = attributeMapByLabel[label];

              if (!attributeDefinition) {
                console.error(
                  `No attribute definition found for CSV header: ${label}`
                );
                acc[label] = attributeValue;

                return acc;
              }

              const { name } = attributeDefinition;
              acc[name] = attributeValue;

              return acc;
            },
            {}
          )
        )
        .map(entity => {
          const { email } = entity;
          const entityExisting = entityMapByEmail[email];

          if (!entityExisting) {
            return entity;
          }

          const { entityId } = entityExisting;

          return Object.assign(entity, { entityId });
        });

      return entityListImport;
    },
    entityMapByEmail() {
      const { entityList } = this;

      return keyBy(entityList, "email");
    },
    ...mapGetters({
      filterAttribute: "attribute/filter",
    }),
    ...mapState({
      file: state => state.file2.record,
    }),
  },
  async created() {
    const { entityName } = this;

    try {
      await this.entityGetList({ reload: false });
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: `Unable to load ${entityName} list` });
      return;
    }
  },
  data() {
    return {
      fileUpload: undefined,
      parseFail: false,
      ruleList: [
        value =>
          !value || value.size < 2e6 || "User upload file must be under 2 MB",
      ],
      step: 1,
      entityResultList: [],
    };
  },
  methods: {
    async entityGetList(options) {
      return this.$store.dispatch(`${this.entityName}/list`, options);
    },
    async fileGet(fileId) {
      const { entityName } = this;
      this.parseFail = false;

      let response;
      try {
        response = await axios({
          method: "get",
          params: { entityName },
          url: `/api/file2/${fileId}`,
        });
      } catch (e) {
        this.parseFail = true;
        let message = `Error parsing ${entityName} import file with fileId ${fileId}`;
        if (e.response) {
          ({ message } = e.response.data);
        }

        this.$notifyError({ message });
        return;
      }

      const {
        data: { result },
      } = response;

      this.setFile(result);
    },
    async upload() {
      const { entityName } = this;

      const formData = new FormData();

      formData.append("file", this.fileUpload);
      formData.append("fileTypeId", 7);

      let response;
      try {
        response = await axios({
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          method: "post",
          url: "/api/file2",
        });
      } catch (e) {
        let message = `Error uploading ${entityName} import file`;
        if (e.response) {
          message = e.response.data;
        }

        this.$notifyError({ message });
        return;
      }

      const { result: fileId } = response.data;

      this.$router.push({
        name: `${entityName}ImportFileConfirm`,
        params: { fileId },
      });
    },
    ...mapActions({
      attributeGetList: "attribute/list",
    }),
    ...mapMutations({
      setFile: "file2/setRecord",
    }),
  },
  props: {
    entityName: {
      required: true,
      type: String,
    },
    fileId: {
      type: Number,
    },
  },
  watch: {
    fileId: async function (fileId) {
      await this.fileGet(fileId);
      this.step = 2;
    },
  },
};
</script>
